import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Download, Loader2, Image as ImageIcon, Video as VideoIcon, FileText } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useConnectionVersion } from '@/hooks/useConnectionVersion';

interface MediaMessageProps {
  messageId: string;
  waMessageId: string;
  mediaType: 'image' | 'video' | 'document';
  existingProcessedUrl?: string | null;
  onUrlProcessed?: (url: string) => void;
  isSending?: boolean;
  fileName?: string;
}

const MediaMessage: React.FC<MediaMessageProps> = ({
  messageId,
  waMessageId,
  mediaType,
  existingProcessedUrl,
  onUrlProcessed,
  isSending = false,
  fileName
}) => {
  const { user } = useAuth();
  const { getUrl } = useConnectionVersion();
  const [mediaUrl, setMediaUrl] = useState<string | null>(existingProcessedUrl || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const instance = (user?.account as any)?.instance;
  const apiKey = (user?.account as any)?.api_token;

  // URLs we know we cannot download/display: WAHA internal address (localhost) and
  // the encrypted WhatsApp CDN (mmg.whatsapp.net). The underlying file is already gone,
  // so there's no point offering a "load" button that would only fail.
  const isUnreachableUrl = !!mediaUrl && typeof mediaUrl === 'string' &&
    (mediaUrl.includes('localhost') || mediaUrl.includes('127.0.0.1') || mediaUrl.includes('mmg.whatsapp.net'));
  const needsProcessing = !mediaUrl;

  useEffect(() => {
    if (existingProcessedUrl) {
      setMediaUrl(existingProcessedUrl);
    }
  }, [existingProcessedUrl]);

  // Pick the storage bucket from the REAL mime type returned by the edge function,
  // not from the message's media_kind (which can diverge and send e.g. a jpeg to chat-videos).
  const getBucketFromMime = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return 'chat-images';
    if (mimeType.startsWith('video/')) return 'chat-videos';
    if (mimeType.startsWith('audio/')) return 'chat-audios';
    return 'chat-documents';
  };

  const getFileExtension = (mimeType: string) => {
    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg';
    if (mimeType.includes('png')) return 'png';
    if (mimeType.includes('gif')) return 'gif';
    if (mimeType.includes('webp')) return 'webp';
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('quicktime')) return 'mov';
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('word')) return 'docx';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'xlsx';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('wav')) return 'wav';
    if (mimeType.startsWith('audio/') && mimeType.includes('mpeg')) return 'mp3';
    return 'bin';
  };

  const downloadAndProcessMedia = async () => {
    if (!supabase) {
      setError('Supabase não disponível');
      return;
    }

    console.log(`📎 [MediaMessage] Starting ${mediaType} processing`);
    console.log('  - messageId:', messageId);
    console.log('  - waMessageId:', waMessageId);
    console.log('  - mediaType:', mediaType);

    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const edgeFunctionUrl = getUrl('process-media-from-payload');

      console.log('📤 [MediaMessage] Calling edge function:', edgeFunctionUrl);

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageId: messageId
        })
      });

      console.log('📥 [MediaMessage] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [MediaMessage] Error response:', errorData);
        throw new Error(errorData.error || 'Erro ao buscar mídia');
      }

      const data = await response.json();
      console.log('✅ [MediaMessage] Data received:', {
        hasBase64: !!data.base64,
        base64Length: data.base64?.length,
        mimetype: data.mimetype,
        fileName: data.fileName,
        media_kind: data.media_kind
      });

      if (!data.base64) {
        throw new Error('Base64 não encontrado na resposta');
      }

      const base64Data = data.base64.includes(',')
        ? data.base64.split(',')[1]
        : data.base64;

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const mimeType = data.mimetype || `${mediaType}/*`;
      const blob = new Blob([byteArray], { type: mimeType });

      const extension = getFileExtension(mimeType);
      const fileName = data.fileName || `${mediaType}_${Date.now()}.${extension}`;
      const filePath = `${messageId}/${fileName}`;
      const bucket = getBucketFromMime(mimeType);

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, blob, {
          contentType: mimeType,
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;
      setMediaUrl(publicUrl);

      if (onUrlProcessed) {
        onUrlProcessed(publicUrl);
      }

      await supabase
        .from('account_messages')
        .update({
          processed_media_url: publicUrl,
          media_url: publicUrl
        })
        .eq('id', messageId);

    } catch (err: any) {
      console.error('Erro ao processar mídia:', err);
      setError(err.message || 'Erro ao processar mídia');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    switch (mediaType) {
      case 'image':
        return <ImageIcon className="w-4 h-4" />;
      case 'video':
        return <VideoIcon className="w-4 h-4" />;
      case 'document':
        return <FileText className="w-4 h-4" />;
    }
  };

  const getLabel = () => {
    switch (mediaType) {
      case 'image':
        return 'Carregar Imagem';
      case 'video':
        return 'Carregar Vídeo';
      case 'document':
        return 'Carregar Documento';
    }
  };

  if (error) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 p-3 bg-red-900/20 rounded-lg border border-red-800/30">
          <span className="text-sm text-red-400">{error}</span>
        </div>
        <Button
          onClick={downloadAndProcessMedia}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (isSending) {
    return (
      <div className="flex items-center gap-2 p-3 bg-neutral-800/50 rounded-lg border border-neutral-700">
        <FileText className="w-8 h-8 text-blue-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-neutral-50">{fileName || 'Documento'}</p>
          <div className="flex items-center gap-2 mt-1">
            <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
            <p className="text-xs text-neutral-400">Enviando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isUnreachableUrl) {
    return (
      <div className="flex items-center gap-2 p-3 bg-neutral-800/50 rounded-lg border border-neutral-700">
        {getIcon()}
        <span className="text-sm text-neutral-400">Mídia indisponível</span>
      </div>
    );
  }

  if (needsProcessing) {
    return (
      <div className="flex items-center gap-2">
        <Button
          onClick={downloadAndProcessMedia}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          size="sm"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processando...
            </>
          ) : (
            <>
              {getIcon()}
              <span className="ml-2">{getLabel()}</span>
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full max-w-sm">
      {mediaType === 'image' && (
        <img
          src={mediaUrl}
          alt="Imagem do chat"
          className="w-full rounded-lg border border-neutral-700"
          loading="lazy"
        />
      )}

      {mediaType === 'video' && (
        <video
          src={mediaUrl}
          controls
          className="w-full rounded-lg border border-neutral-700"
          preload="metadata"
        />
      )}

      {mediaType === 'document' && (
        <div className="flex items-center gap-2 p-3 bg-neutral-800/50 rounded-lg border border-neutral-700">
          <FileText className="w-8 h-8 text-blue-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-neutral-50">{fileName || 'Documento'}</p>
            <p className="text-xs text-neutral-400">Clique para visualizar</p>
          </div>
        </div>
      )}

      <a
        href={mediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 self-start"
      >
        <Download className="w-3 h-3" />
        {mediaType === 'document' ? 'Abrir' : 'Download'}
      </a>
    </div>
  );
};

export default MediaMessage;
